import type { ElementType, PropsWithChildren } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import type { PolymorphicProps } from "shared/types";

import { cn } from "shared/utils/cn";

import { Spinner } from "./spinner";

const buttonVariants = cva(
  [
    "relative select-none cursor-pointer outline-hidden inline-flex grow-0 items-center justify-center transition-colors",
    "hover:brightness-105 active:brightness-105",
  ],
  {
    variants: {
      theme: {
        primary: ["text-white bg-linear-to-r from-[#61C9FF] to-[#47AAFF]"],
        secondary: ["bg-dark-grey text-white"],
        white: [
          "text-black bg-white",
          "hover:brightness-90 active:brightness-90",
        ],
        text: ["bg-transparent text-white"],
        transparent: [
          "bg-transparent border text-white border-pale-grey/50 hover:bg-white/5 hover:border-pale-grey",
        ],
        danger: ["bg-pale-red hover:brightness-110 text-white"],
      },
      rounded: {
        true: "rounded-full! p-0!",
        false: "",
      },
      size: {
        "30": "h-7.5 min-h-7.5 max-h-h-7.5 px-2.5 rounded-full",
        "34": "h-8.5 max-h-8.5 min-h-8.5 px-4.5 font-medium text-sm leading-5.5 tracking-[0.4px] rounded-4xl",
        "46": "h-11.5 max-h-11.5 min-h-11.5 px-5 text-lg leading-5.5 tracking-[0.4px] font-medium rounded-xl",
        "52": "h-13 max-h-13 min-h-13 px-5 text-lg leading-5.5 rounded-xl tracking-[0.4px] font-medium",
      },
      disabled: {
        true: "pointer-events-none opacity-50",
        false: null,
      },
    },
    compoundVariants: [
      // {
      //   theme: "primary",
      //   disabled: true,
      //   className: "from-oil to-oil text-white/75",
      // },
      { size: "46", rounded: true, className: "size-11.5" },
      { size: "34", rounded: true, className: "size-8.5" },
      { size: "30", rounded: true, className: "size-7.5" },
    ],
    defaultVariants: {
      theme: "primary",
      size: "46",
      rounded: false,
    },
  }
);

const spinnerVariants = cva([], {
  variants: {
    buttonTheme: {
      primary: [],
      secondary: [],
      white: ["text-baltic-sea"],
      text: [],
      transparent: [],
      danger: [],
    },
  },
});

type Props = VariantProps<typeof buttonVariants> & {
  isLoading?: boolean;
  className?: string;
  spinnerClassName?: string;
};

export type ButtonProps<E extends ElementType> = PolymorphicProps<E, Props>;

export const Button = <E extends ElementType = "button">(
  buttonProps: PropsWithChildren<ButtonProps<E>>
) => {
  const {
    disabled,
    className,
    isLoading = false,
    theme,
    rounded = false,
    size,
    as = "button",
    children,
    spinnerClassName,
    ...props
  } = buttonProps;
  const Comp = as;

  return (
    <Comp
      type="button"
      className={cn(
        buttonVariants({ theme, size, rounded, disabled }),
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner
            className={cn(
              spinnerVariants({ buttonTheme: theme }),
              "size-5",
              spinnerClassName
            )}
          />
        </span>
      )}
      {isLoading ? <span className="opacity-0">{children}</span> : children}
    </Comp>
  );
};
